import { Helmet } from "react-helmet-async";
import { SiteSchema } from "@/lib/schema";

const baseUrl = "https://ecosafe.com.sa";
const siteName = "ECOSAFE";
const defaultDescription =
  "ECOSAFE provides professional training and consulting services across Saudi Arabia and beyond. Transform your organization and individuals through development excellence.";
const defaultImage = "/hero-training.jpg";
const twitterHandle = "@ecosafe";

interface HeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article" | "course";
  structuredData?: object | object[];
  noIndex?: boolean;
  noFollow?: boolean;
}

const Head = ({
  title,
  description,
  image,
  url,
  type = "website",
  structuredData,
  noIndex = false,
  noFollow = false,
}: HeadProps) => {
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const fullDescription = (description || defaultDescription).replace(/\s+/g, " ").trim().slice(0, 160);
  const fullImage = image || defaultImage;
  const fullUrl = url || baseUrl;
  const fullImageUrl = fullImage.startsWith("http") ? fullImage : `${baseUrl}${fullImage}`;

  const robotsContent = [
    noIndex ? "noindex" : "index",
    noFollow ? "nofollow" : "follow",
  ].join(", ");

  const jsonLd = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  if (jsonLd.length === 0) jsonLd.push(SiteSchema);

  return (
    <Helmet
      title={fullTitle}
      meta={[
        { name: "description", content: fullDescription },
        { name: "robots", content: robotsContent },
        { property: "og:title", content: title || siteName },
        { property: "og:description", content: fullDescription },
        { property: "og:image", content: fullImageUrl },
        { property: "og:type", content: type },
        { property: "og:url", content: fullUrl },
        { property: "og:site_name", content: siteName },
        { property: "og:locale", content: "en_SA" },
        { property: "og:locale:alternate", content: "ar_SA" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: twitterHandle },
        { name: "twitter:title", content: title || siteName },
        { name: "twitter:description", content: fullDescription },
        { name: "twitter:image", content: fullImageUrl },
        { name: "theme-color", content: "#84CC16" },
      ]}
      link={[
        { rel: "canonical", href: fullUrl },
      ]}
      script={jsonLd.map((data) => ({
        type: "application/ld+json",
        innerHTML: JSON.stringify(data),
      }))}
    />
  );
};

export default Head;